/**
 * Keys handed over at move-in / move-out (handover.protocol payload `keys`). Stored as a list of key types with a
 * count; `label` names a free-text key ("Tor-Fernbedienung") and is only used with type `other`. Older protocols
 * stored a bare total (number): `handoverKeys` migrates that on read, so readers only deal with the list.
 */
export const HANDOVER_KEY_TYPES = [
  'front_door',
  'apartment_door',
  'mailbox',
  'cellar',
  'attic',
  'garage',
  'bike_cellar',
  'laundry',
  'other',
] as const;
export type HandoverKeyType = (typeof HANDOVER_KEY_TYPES)[number];
export type HandoverKey = { type: HandoverKeyType; label?: string; count: number };

const isKey = (k: unknown): k is HandoverKey => {
  if (!k || typeof k !== 'object') return false;
  const x = k as HandoverKey;
  return (HANDOVER_KEY_TYPES as readonly string[]).includes(x.type) && Number.isInteger(x.count) && x.count > 0;
};

/** Keys of a stored handover payload as a list; a legacy total becomes one unnamed `other` row. */
export function handoverKeys(raw: unknown): HandoverKey[] {
  if (typeof raw === 'number') return Number.isInteger(raw) && raw > 0 ? [{ type: 'other', count: raw }] : [];
  return Array.isArray(raw) ? raw.filter(isKey) : [];
}

/** Total number of keys of a stored handover payload (legacy number or list); undefined when none were recorded. */
export function handoverKeyCount(raw: unknown): number | undefined {
  if (raw == null) return undefined;
  if (typeof raw === 'number') return raw;
  return handoverKeys(raw).reduce((s, k) => s + k.count, 0);
}

// ───────────── Übergabeprotokoll (ADR-0025) ─────────────

export const HANDOVER_DIRECTIONS = ['move_in', 'move_out'] as const;
export type HandoverDirection = (typeof HANDOVER_DIRECTIONS)[number];
export const HANDOVER_STATUSES = ['draft', 'completed'] as const;
export type HandoverStatus = (typeof HANDOVER_STATUSES)[number];
export const HANDOVER_ROOM_KINDS = ['hallway', 'living', 'bedroom', 'kids', 'kitchen', 'bathroom', 'wc', 'storage', 'balcony', 'terrace', 'garden', 'cellar', 'attic', 'garage', 'other'] as const;
export type HandoverRoomKind = (typeof HANDOVER_ROOM_KINDS)[number];
/** What is checked in a room (one row per element). */
export const HANDOVER_ELEMENTS = ['walls', 'ceiling', 'floor', 'windows', 'doors', 'heating', 'electrics', 'sanitary', 'tiles', 'kitchen_fittings', 'smoke_detector', 'other'] as const;
export type HandoverElement = (typeof HANDOVER_ELEMENTS)[number];
/** good = einwandfrei, used = normale Gebrauchsspuren, defect = Mangel/Schaden; null = not checked yet. */
export const HANDOVER_CONDITIONS = ['good', 'used', 'defect'] as const;
export type HandoverCondition = (typeof HANDOVER_CONDITIONS)[number];
/** Who has to fix a defect: the tenant, the landlord, or not agreed (strittig/offen). */
export const DEFECT_RESPONSIBILITIES = ['tenant', 'landlord', 'open'] as const;
export type DefectResponsibility = (typeof DEFECT_RESPONSIBILITIES)[number];
export const HANDOVER_CLEANING = ['clean', 'swept', 'not_clean'] as const;
export type HandoverCleaning = (typeof HANDOVER_CLEANING)[number];
export const HANDOVER_ROLES = ['landlord', 'tenant', 'representative', 'witness'] as const;
export type HandoverRole = (typeof HANDOVER_ROLES)[number];
/** drawn = signed on the screen; absent / refused = a tenant who did not sign (recorded with a note). */
export const SIGNATURE_METHODS = ['drawn', 'absent', 'refused'] as const;
export type SignatureMethod = (typeof SIGNATURE_METHODS)[number];
export const HANDOVER_CHANNELS = ['email', 'app'] as const;
export type HandoverChannel = (typeof HANDOVER_CHANNELS)[number];

/**
 * A signature is stored as vector strokes, never as an image: each stroke is a flat list x0,y0,x1,y1,… of integers in a
 * box of SIGNATURE_BOX.w × SIGNATURE_BOX.h (clients scale their canvas to it). PDFs draw the lines again.
 */
export const SIGNATURE_BOX = { w: 1000, h: 400 } as const;
export const HANDOVER_LIMITS = { rooms: 40, itemsPerRoom: 40, photosPerSpot: 30, photosTotal: 300, meters: 50, attendees: 12, strokes: 80, strokePoints: 2000 } as const;

export type HandoverDefect = { description: string; responsibility: DefectResponsibility; repairBy?: string; estimatedCostCents?: number; preexisting: boolean };
export type HandoverItem = { id: string; element: HandoverElement; label?: string; condition: HandoverCondition | null; note?: string; photoIds: string[]; defect?: HandoverDefect };
export type HandoverRoom = { id: string; kind: HandoverRoomKind; label: string; note?: string; photoIds: string[]; items: HandoverItem[] };
export type HandoverMeter = { meterId: string; value: number | null; photoId: string | null; note?: string };
export type HandoverAttendee = { id: string; role: HandoverRole; name: string; personId?: string };
export type HandoverSignature = { attendeeId: string; method: SignatureMethod; strokes?: number[][]; note?: string; signedAt: string; contentHash: string };

/** Elements checked by default per room kind (Bad: Fliesen and Sanitär, Flur/Schlafzimmer: Rauchwarnmelder …). */
export const HANDOVER_ROOM_ELEMENTS: Record<HandoverRoomKind, readonly HandoverElement[]> = {
  hallway: ['walls', 'ceiling', 'floor', 'doors', 'electrics', 'smoke_detector'],
  living: ['walls', 'ceiling', 'floor', 'windows', 'doors', 'heating', 'electrics', 'smoke_detector'],
  bedroom: ['walls', 'ceiling', 'floor', 'windows', 'doors', 'heating', 'electrics', 'smoke_detector'],
  kids: ['walls', 'ceiling', 'floor', 'windows', 'doors', 'heating', 'electrics', 'smoke_detector'],
  kitchen: ['walls', 'ceiling', 'floor', 'windows', 'doors', 'heating', 'electrics', 'tiles', 'kitchen_fittings'],
  bathroom: ['walls', 'ceiling', 'floor', 'windows', 'doors', 'heating', 'electrics', 'tiles', 'sanitary'],
  wc: ['walls', 'ceiling', 'floor', 'doors', 'electrics', 'tiles', 'sanitary'],
  storage: ['walls', 'ceiling', 'floor', 'doors', 'electrics'],
  balcony: ['floor', 'walls', 'doors'],
  terrace: ['floor', 'walls', 'doors'],
  garden: ['other'],
  cellar: ['walls', 'ceiling', 'floor', 'doors', 'electrics'],
  attic: ['walls', 'ceiling', 'floor', 'doors', 'electrics'],
  garage: ['walls', 'ceiling', 'floor', 'doors', 'electrics'],
  other: ['walls', 'ceiling', 'floor', 'windows', 'doors'],
};

/** Room list of a new protocol when nothing better is known (a flat; parking/storage get one room). */
export const DEFAULT_HANDOVER_ROOMS: Record<'apartment' | 'commercial' | 'parking' | 'storage', readonly HandoverRoomKind[]> = {
  apartment: ['hallway', 'living', 'bedroom', 'kitchen', 'bathroom'],
  commercial: ['hallway', 'other', 'wc'],
  parking: ['garage'],
  storage: ['storage'],
};

/** Room kind guessed from a room name of the Flächenrechner ("Bad", "Küche", "Kinderzimmer 2" …). */
export function roomKindFromName(name: string): HandoverRoomKind {
  const n = name.toLowerCase();
  const rules: [RegExp, HandoverRoomKind][] = [
    [/g(ä|ae)ste|wc|toilette/, 'wc'],
    [/bad|dusche/, 'bathroom'],
    [/k(ü|ue)che|kochen/, 'kitchen'],
    [/flur|diele|eingang|korridor/, 'hallway'],
    [/kind/, 'kids'],
    [/schlaf/, 'bedroom'],
    [/wohn|ess|arbeit|b(ü|ue)ro|zimmer/, 'living'],
    [/balkon|loggia/, 'balcony'],
    [/terrasse/, 'terrace'],
    [/garten/, 'garden'],
    [/keller/, 'cellar'],
    [/dach|speicher/, 'attic'],
    [/garage|stellplatz/, 'garage'],
    [/abstell|hwr|hauswirtschaft|speis|vorrat/, 'storage'],
  ];
  return rules.find(([rx]) => rx.test(n))?.[1] ?? 'other';
}

/** A new room with its default elements, nothing checked yet. `newId` makes ids unique within the protocol. */
export function newHandoverRoom(kind: HandoverRoomKind, label: string, newId: () => string): HandoverRoom {
  return { id: newId(), kind, label, photoIds: [], items: HANDOVER_ROOM_ELEMENTS[kind].map((element) => ({ id: newId(), element, condition: null, photoIds: [] })) };
}

/**
 * The structure of a protocol taken over from an earlier one (move-out from the move-in, move-in from the previous
 * tenant's move-out): same rooms and item ids (so both protocols can be compared row by row), nothing checked, no
 * photos; a defect of the earlier protocol is kept as `preexisting` reference only on the earlier protocol.
 */
export function roomsFromEarlier(rooms: readonly HandoverRoom[]): HandoverRoom[] {
  return rooms.map((r) => ({ id: r.id, kind: r.kind, label: r.label, photoIds: [], items: r.items.map((i) => ({ id: i.id, element: i.element, ...(i.label ? { label: i.label } : {}), condition: null, photoIds: [] })) }));
}

/** All photo ids a protocol refers to (rooms, items, meters, general). */
export function handoverPhotoIds(h: { rooms: readonly HandoverRoom[]; meters: readonly HandoverMeter[]; photoIds: readonly string[] }): string[] {
  return [...new Set([...h.photoIds, ...h.rooms.flatMap((r) => [...r.photoIds, ...r.items.flatMap((i) => i.photoIds)]), ...h.meters.flatMap((m) => (m.photoId ? [m.photoId] : []))])];
}

/** Defects of a protocol with their room, in protocol order. */
export function handoverDefects(rooms: readonly HandoverRoom[]): { room: HandoverRoom; item: HandoverItem; defect: HandoverDefect }[] {
  return rooms.flatMap((room) => room.items.flatMap((item) => (item.condition === 'defect' ? [{ room, item, defect: item.defect ?? { description: '', responsibility: 'open' as const, preexisting: false } }] : [])));
}

/**
 * New damage at the move-out the tenant answers for (not known at the move-in): suggestions for deductions of the deposit
 * (docs/18 V5). Only defects with an estimate; the landlord decides in the settlement.
 */
export function depositSuggestions(rooms: readonly HandoverRoom[], elementLabel: (e: HandoverElement) => string): { label: string; amountCents: number }[] {
  return handoverDefects(rooms)
    .filter(({ defect }) => defect.responsibility === 'tenant' && !defect.preexisting && (defect.estimatedCostCents ?? 0) > 0)
    .map(({ room, item, defect }) => ({ label: `${room.label}: ${item.label || elementLabel(item.element)}${defect.description ? ` – ${defect.description}` : ''}`.slice(0, 120), amountCents: defect.estimatedCostCents! }));
}

export type HandoverIssueCode =
  | 'date_before_start'
  | 'date_after_end'
  | 'no_landlord'
  | 'no_tenant'
  | 'landlord_signature_missing'
  | 'tenant_signature_missing'
  | 'signature_outdated'
  | 'defect_description_missing'
  | 'repair_by_before_date'
  | 'photo_not_uploaded'
  | 'reading_conflict'
  | 'meter_reading_missing'
  | 'reading_below_previous'
  | 'items_unchecked'
  | 'keys_missing'
  | 'cleaning_missing'
  | 'move_out_not_recorded'
  | 'date_far_from_start'
  | 'date_far_from_end'
  | 'tenant_absent'
  | 'tenant_refused'
  | 'forwarding_address_missing';
export type HandoverIssue = { code: HandoverIssueCode; severity: 'error' | 'warning'; ref?: string; params?: Record<string, string | number> };

export type HandoverCheckInput = {
  direction: HandoverDirection;
  date: string;
  rooms: readonly HandoverRoom[];
  meters: readonly HandoverMeter[];
  keys: readonly unknown[];
  cleaning: HandoverCleaning | null;
  attendees: readonly HandoverAttendee[];
  signatures: readonly HandoverSignature[];
  forwardingAddress?: { street?: string; zip?: string; city?: string } | null;
  /** A signed paper protocol was uploaded: the on-screen signatures are not needed. */
  paper: boolean;
  tenancy: { start: string; end: string | null };
  /** Active meters of the unit on the day (the protocol should have a reading for each). */
  activeMeterIds: readonly string[];
  /** Hash of the current content; a drawn signature with another hash was given for an older version. */
  contentHash?: string;
};

const dayDiff = (a: string, b: string) => Math.round((Date.parse(`${a}T00:00:00Z`) - Date.parse(`${b}T00:00:00Z`)) / 86_400_000);

/**
 * What blocks completing a protocol (errors) and what should be looked at (warnings). Pure: the server runs it before
 * completing (plus its own checks: uploads, readings), the apps show the same list live. Warnings never block
 * (docs/18 rule 5: "kritisch statt blockierend").
 */
export function handoverIssues(h: HandoverCheckInput): HandoverIssue[] {
  const out: HandoverIssue[] = [];
  const err = (code: HandoverIssueCode, ref?: string, params?: HandoverIssue['params']) => out.push({ code, severity: 'error', ...(ref ? { ref } : {}), ...(params ? { params } : {}) });
  const warn = (code: HandoverIssueCode, ref?: string, params?: HandoverIssue['params']) => out.push({ code, severity: 'warning', ...(ref ? { ref } : {}), ...(params ? { params } : {}) });

  if (h.date < h.tenancy.start && dayDiff(h.tenancy.start, h.date) > 31) err('date_before_start', undefined, { start: h.tenancy.start });
  else if (h.direction === 'move_out' && h.date < h.tenancy.start) err('date_before_start', undefined, { start: h.tenancy.start });
  if (h.direction === 'move_in' && h.tenancy.end && h.date > h.tenancy.end) err('date_after_end', undefined, { end: h.tenancy.end });

  const landlords = h.attendees.filter((a) => a.role === 'landlord' || a.role === 'representative');
  const tenants = h.attendees.filter((a) => a.role === 'tenant');
  if (!landlords.length) err('no_landlord');
  if (!tenants.length) err('no_tenant');
  if (!h.paper) {
    const sig = (id: string) => h.signatures.find((s) => s.attendeeId === id);
    const signedLandlord = landlords.some((a) => sig(a.id)?.method === 'drawn');
    if (landlords.length && !signedLandlord) err('landlord_signature_missing');
    for (const t of tenants) {
      const s = sig(t.id);
      if (!s) err('tenant_signature_missing', t.id, { name: t.name });
      else if (s.method === 'absent') warn('tenant_absent', t.id, { name: t.name });
      else if (s.method === 'refused') warn('tenant_refused', t.id, { name: t.name });
    }
    if (h.contentHash && h.signatures.some((s) => s.method === 'drawn' && s.contentHash !== h.contentHash)) err('signature_outdated');
  }

  let unchecked = 0;
  for (const room of h.rooms)
    for (const item of room.items) {
      if (item.condition === null) unchecked++;
      if (item.condition !== 'defect') continue;
      if (!item.defect?.description.trim()) err('defect_description_missing', item.id, { room: room.label });
      if (item.defect?.repairBy && item.defect.repairBy < h.date) err('repair_by_before_date', item.id, { room: room.label });
    }
  if (unchecked) warn('items_unchecked', undefined, { count: unchecked });

  for (const id of h.activeMeterIds) if (!h.meters.some((m) => m.meterId === id && m.value !== null)) warn('meter_reading_missing', id);
  if (!h.keys.length) warn('keys_missing');
  if (!h.cleaning && h.direction === 'move_out') warn('cleaning_missing');
  if (h.direction === 'move_out') {
    if (!h.tenancy.end) warn('move_out_not_recorded');
    else if (Math.abs(dayDiff(h.date, h.tenancy.end)) > 14) warn('date_far_from_end', undefined, { end: h.tenancy.end });
    const a = h.forwardingAddress;
    if (!a?.street?.trim() || !a.city?.trim()) warn('forwarding_address_missing');
  } else if (Math.abs(dayDiff(h.date, h.tenancy.start)) > 14) warn('date_far_from_start', undefined, { start: h.tenancy.start });
  return out;
}

/** True when nothing blocks completing. */
export const handoverCompletable = (issues: readonly HandoverIssue[]) => !issues.some((i) => i.severity === 'error');
